/**
 * Types for Aircraft Schedules — Movement Ledger Model
 *
 * Each movement is a directional record (outbound/inbound) with a single
 * scheduled_at timestamp. Outbound + inbound pairs linked by pair_id.
 * Schedules are aircraft-level facts; allocation relationship derived via temporal join.
 */

import type { BadgeProps } from '@/components/ui/badge'

// ============================================
// Type Unions (matching TEXT CHECK constraints in DB)
// ============================================

export type ScheduleDirection = 'outbound' | 'inbound'
export type ScheduleSource = 'flight_plan' | 'owner_reported' | 'csr_entered' | 'pattern_inferred' | 'recurring_rule' | 'integration'
export type ScheduleConfidence = 'confirmed' | 'tentative' | 'predicted'
export type ScheduleStatus = 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show'

// ============================================
// Arrays for Iteration
// ============================================

export const SCHEDULE_DIRECTIONS = ['outbound', 'inbound'] as const
export const SCHEDULE_SOURCES = ['flight_plan', 'owner_reported', 'csr_entered', 'pattern_inferred', 'recurring_rule', 'integration'] as const
export const SCHEDULE_CONFIDENCES = ['confirmed', 'tentative', 'predicted'] as const
export const SCHEDULE_STATUSES = ['scheduled', 'confirmed', 'completed', 'cancelled', 'no_show'] as const

// ============================================
// Labels
// ============================================

export const SCHEDULE_DIRECTION_LABELS: Record<ScheduleDirection, string> = {
  outbound: 'Departure',
  inbound: 'Return',
}

export const SCHEDULE_SOURCE_LABELS: Record<ScheduleSource, string> = {
  flight_plan: 'Flight Plan',
  owner_reported: 'Owner',
  csr_entered: 'CSR',
  pattern_inferred: 'Pattern',
  recurring_rule: 'Recurring',
  integration: 'Integration',
}

export const SCHEDULE_CONFIDENCE_LABELS: Record<ScheduleConfidence, string> = {
  confirmed: 'Confirmed',
  tentative: 'Tentative',
  predicted: 'Predicted',
}

export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
}

// ============================================
// Badge Variants
// ============================================

type BadgeVariant = BadgeProps['variant']

export const SCHEDULE_CONFIDENCE_VARIANTS: Record<ScheduleConfidence, BadgeVariant> = {
  confirmed: 'default',
  tentative: 'secondary',
  predicted: 'outline',
}

export const SCHEDULE_STATUS_VARIANTS: Record<ScheduleStatus, BadgeVariant> = {
  scheduled: 'secondary',
  confirmed: 'default',
  completed: 'outline',
  cancelled: 'destructive',
  no_show: 'destructive',
}

export const SCHEDULE_SOURCE_VARIANTS: Record<ScheduleSource, BadgeVariant> = {
  flight_plan: 'default',
  owner_reported: 'secondary',
  csr_entered: 'secondary',
  pattern_inferred: 'outline',
  recurring_rule: 'outline',
  integration: 'secondary',
}

// ============================================
// Interfaces
// ============================================

/** Primary contact (dispatcher) for a schedule's owner client. */
export interface ScheduleContactInfo {
  id: string
  firstName: string | null
  lastName: string | null
  jobTitle: string | null
  emailPrimary: string | null
  phoneMobile: string | null
}

/** Owner/operator client of a schedule's aircraft (the FlightBridge operator). */
export interface ScheduleOwnerInfo {
  id: string
  clientType: string | null
  companyName: string | null
  firstName: string | null
  lastName: string | null
  emailPrimary: string | null
  phonePrimary: string | null
  /** Display name: company name for corporates, "First Last" for individuals. */
  displayName: string | null
  /** Primary contact, if one exists. */
  primaryContact: ScheduleContactInfo | null
}

export interface ScheduleAircraftInfo {
  id: string
  tailNumber: string
  manufacturer: string | null
  model: string | null
  typeCode: string | null
  /** FK to the owner client (clients.id) — null when unlinked. */
  ownerClientId: string | null
  /** Owner client + primary contact — populated by fetchLocationSchedulesForDate. */
  owner: ScheduleOwnerInfo | null
}

export interface AircraftSchedule {
  id: string
  aircraftId: string
  locationId: string
  organizationId: string
  direction: ScheduleDirection
  scheduledAt: string
  actualAt: string | null
  eventId: string | null
  pairId: string | null
  airportIcao: string | null
  airportName: string | null
  source: ScheduleSource
  confidence: ScheduleConfidence
  status: ScheduleStatus
  flightReference: string | null
  notes: string | null
  passengerCount: number | null
  crewCount: number | null
  divertedToIcao: string | null
  servicesRequested: unknown | null
  createdAt: string
  /** Nested aircraft info — populated by fetchLocationSchedulesForDate */
  aircraft: ScheduleAircraftInfo | null
  /** Integration provider code — populated by fetchLocationSchedulesForDate */
  providerCode: string | null
}

export interface AircraftScheduleFormData {
  aircraft_id: string
  location_id: string
  direction: ScheduleDirection
  scheduled_at: string
  source: ScheduleSource
  confidence?: ScheduleConfidence
  status?: ScheduleStatus
  actual_at?: string
  event_id?: string
  pair_id?: string
  airport_icao?: string
  airport_name?: string
  flight_reference?: string
  notes?: string
  passenger_count?: number
  crew_count?: number
  diverted_to_icao?: string
  services_requested?: unknown
}
